import React from 'react';

import { Col } from 'antd';
import { config } from 'config/config';
import moment from 'moment';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import {
  IResourceBehavior,
  IResourceDetailList,
  IResourceList,
} from 'src/components/challenge/ChallengeInterfaces';
import { renderChallengeCampaign } from 'src/pages/campaign';
import { renderChallengeContest } from 'src/pages/challenge';
import { renderChallengeExperience } from 'src/pages/experience';
import { renderChallengeTalk } from 'src/pages/talk';
import { renderChallengeTraining } from 'src/pages/training';
import { renderChallengeFormation } from 'src/pages/formation';
import {
  CampaignModuleStatusEnum,
  ChallengeTypeEnum,
  IResourceBehaviorsEnum,
  MaterialsEnum,
  PlatformEnum,
  ResourceTypeDetailEnum,
  ResourceTypeEnum,
  UserResourceEnum,
} from 'src/shared/enums';
import {
  ChallengeDetail,
  IAddressData,
  IAddressDataChile,
  IChallenge,
  IChallengeEvent,
  IChallengeQuiz,
  IChallengeResource,
  IChallengeResourceConfig,
  IChallengeRole,
  IResourceDependency,
  ISurvey,
  ISurveyResponse,
  IUser,
} from 'src/shared/models';
import { RoleEnum } from 'src/shared/enums/role.enum';

const {
  ACTIVATION_BUTTON,
  ATTEND,
  CHALLENGE_MATERIALS,
  CHECKOUT,
  CHECKOUT_LOTTERY,
  CHECKOUT_DONATION,
  EVENT,
  EXPERIENCE_ACTIVATION,
  LINKED_CHALLENGES,
  PARTICIPATION,
  QUIZ,
  QUIZ_DERMO,
  QUIZ_TYPEFORM,
  REDEEM_PRODUCT,
  SHOPPING_CART,
  SPEAKER,
  SURVEY,
  VIDEO,
} = ResourceTypeEnum;

export const constructResources = (resources: IResourceList[]) => {
  const recursiveResource: IResourceList[] = [];
  const sortedResources: IResourceList[] = [];

  resources
    .filter((_resource) => _resource.status)
    .sort(function (a: IResourceList, b: IResourceList) {
      return a.idResource - b.idResource;
    })
    .sort(function (a: IResourceList, b: IResourceList) {
      return a.order - b.order;
    })
    .forEach((resource: IResourceList) => {
      if (resource.resourceDetailList)
        resource.resourceDetailList.sort(function (
          a: IResourceDetailList,
          b: IResourceDetailList,
        ) {
          return a.idResourceD - b.idResourceD;
        });
      if (
        resource.indResource !== null &&
        resource.idResourceType.idResourceType !== SPEAKER
      ) {
        const newResource = { ...resource, resourceList: [] };
        recursiveResource.push(newResource);
      } else {
        const newResource = { ...resource, resourceList: [] };
        sortedResources.push(newResource);
      }
    });

  recursiveResource.forEach((_resource: IResourceList) => {
    recursiveResource.forEach((_destResource) => {
      if (_resource.indResource === _destResource.idResource)
        _destResource.resourceList.push(_resource);
    });
  });

  recursiveResource.forEach((_resource: IResourceList) => {
    sortedResources.forEach((_destResource: IResourceList) => {
      if (_resource.indResource === _destResource.idResource) {
        _destResource.resourceList.push(_resource);
      }
    });
  });

  return sortedResources;
};

export const constructMaterials = (materials: IResourceList[]) => {
  if (!materials.length) return null;

  const getMaterialDetails = (material: IResourceList) => {
    const { resourceDetailList } = material;
    const name = material.name;
    let description, image, content, brand, order, descriptionDetail;
    resourceDetailList
      .filter((detail) => detail.status)
      .forEach((detail) => {
        switch (detail.idResourceTypeD.idResourceTypeD) {
          case ResourceTypeDetailEnum.MATERIAL_BRAND:
            brand = detail.value;
            break;
          case ResourceTypeDetailEnum.MATERIAL_ORDER:
            order = detail.value;
            break;
          case ResourceTypeDetailEnum.MATERIAL_DESCRIPTION:
            descriptionDetail = detail;
            description = detail.value;
            break;
          case ResourceTypeDetailEnum.MATERIAL_CONTENT:
            content = detail.value;
            break;
          case ResourceTypeDetailEnum.MATERIAL_IMAGE:
            image = detail.value;
            break;
          default:
            break;
        }
      });

    return {
      idMaterial: material.idResource,
      type: MaterialsEnum.LEARN_AND_EARN,
      name,
      description,
      image,
      content,
      brand,
      order,
      descriptionDetail,
      resource: material,
    };
  };

  return materials.map((material) => getMaterialDetails(material));
};

export const hasUserParticipated = (resource: IResourceList) => {
  return (
    resource?.idResourceType?.idResourceType === PARTICIPATION &&
    resource.usersM2MList?.length
  );
};

export const hasUserAttended = (resource: IResourceList) => {
  return (
    resource?.idResourceType?.idResourceType === ATTEND &&
    resource.usersM2MList?.length
  );
};

export const checkUserStatus = (
  resource: IResourceList,
  expectedStatus: (CampaignModuleStatusEnum | UserResourceEnum)[],
): boolean => {
  return Boolean(
    resource.usersM2MList.find(({ status, statusResource }) => {
      return status && expectedStatus.includes(statusResource);
    }),
  );
};

export const getResourceReplays = (resource: IResourceList): number => {
  return Number(resource.usersM2MList.filter(({ status }) => !status).length);
};

export const isAttendAvailable = (
  startEventDate: moment.Moment,
  endEventDate: moment.Moment,
) => {
  if (!startEventDate || !endEventDate) return false;

  const currentDate = getCurrentMoment();

  const start = startEventDate
    .clone()
    .subtract(15, 'minutes')
    .format('YYYY-MM-DD HH:mm:ss');

  return moment(currentDate).isBetween(start, endEventDate);
};

export const isInscriptionAvailable = (
  startVisDate: string,
  startEventDate: moment.Moment,
) => {
  if (!startVisDate || !startEventDate) return false;

  const currentDate = getCurrentMoment();

  return moment(currentDate).isBetween(
    startVisDate,
    startEventDate.format('YYYY-MM-DD HH:mm:ss'),
  );
};

export const isEventExpired = (endEventDate: moment.Moment) => {
  if (!endEventDate) return true;

  const currentDate = getCurrentMoment();

  return moment(currentDate).isAfter(
    endEventDate.format('YYYY-MM-DD HH:mm:ss'),
  );
};

export const isEventInProgress = (
  startEventDate: moment.Moment,
  endEventDate: moment.Moment,
) => {
  if (!startEventDate || !endEventDate) return false;

  const currentDate = getCurrentMoment();

  return moment(currentDate).isBetween(
    startEventDate.format('YYYY-MM-DD HH:mm:ss'),
    endEventDate.format('YYYY-MM-DD HH:mm:ss'),
  );
};

const getCurrentMoment = () => {
  return moment(new Date())
    .utcOffset(config.APP.DEFAULT_TIMEZONE, false)
    .format('YYYY-MM-DD HH:mm:ss');
};

export const getBrowserTimezone = () => {
  return new Date().getTimezoneOffset() / 60;
};

export function getSurveyResponseIfExists(
  data: ISurvey,
): ISurveyResponse | null {
  return data?.userResponseList.slice(-1)[0];
}

export function setQuizBehavior(
  config: IChallengeQuiz,
  resourceBehavior: IResourceBehavior,
) {
  // QUIZ BEHAVIOR
  switch (true) {
    case !resourceBehavior:
    case resourceBehavior.status:
      config.disabled = false;
      config.shouldRender = true;
      break;
    case !resourceBehavior.status &&
      resourceBehavior.behavior === IResourceBehaviorsEnum.DISABLED:
      config.shouldRender = true;
      config.disabled = true;
      break;
    case !resourceBehavior.status &&
      resourceBehavior.behavior === IResourceBehaviorsEnum.HIDDEN:
      config.shouldRender = false;
      break;
  }

  return config;
}

export const completeChallengeUserResource = async ({
  dataPath = apiPaths.CHALLENGES.COMPLETE_USER_RESOURCE,
  idResource,
  updateChallenge,
  updateUserPoints,
}: {
  dataPath?: string;
  idResource: number;
  updateUserPoints: (userPoints: number) => void;
  updateChallenge: (challengeData: IChallenge) => void;
}) => {
  try {
    const response = await api.putDataCallById({
      dataPath,
      id: idResource,
    });

    const { score, challenge } = response.data;

    if (typeof score === 'number') updateUserPoints(score);

    updateChallenge(challenge);
  } catch (err) {
    const { code, message } = err?.response?.data || {};

    if (code === '9999' && typeof message === 'number') {
      updateUserPoints(message);
    }
    return err;
  }
};
export const resetChallengeUserResources = async ({
  challengeId,
  updateChallenge,
  updateUserPoints,
}: {
  challengeId: number;
  updateUserPoints: (userPoints: number) => void;
  updateChallenge: (challengeData: IChallenge) => void;
}) => {
  try {
    const response = await api.putDataCallById({
      dataPath: apiPaths.CHALLENGES.REPLAY_CHALLENGE,
      id: challengeId,
    });

    const challenge = response.data;

    updateChallenge(challenge);
  } catch (err) {
    return err;
  }
};

export const completeBeautyGuideResource = async ({
  dataPath = apiPaths.CHALLENGES.COMPLETE_USER_RESOURCE,
  idResource,
  data,
  updateChallenge,
  updateUserPoints,
}: {
  dataPath?: string;
  idResource: number;
  data: { [key: string]: any };
  updateUserPoints: (userPoints: number) => void;
  updateChallenge: (challengeData: IChallenge) => void;
}) => {
  try {
    const response = await api.putDataCallById({
      dataPath,
      id: idResource,
      data,
    });

    const { score, challenge } = response.data;
    if (typeof score === 'number') {
      updateUserPoints(score);
    }
    updateChallenge(challenge);
  } catch (err) {
    const { code, message } = err?.response?.data || {};

    if (code === '9999' && typeof message === 'number') {
      updateUserPoints(message);
    }
    return err;
  }
};

export const completeDonationCheckout = async ({
  dataPath = apiPaths.CHALLENGES.COMPLETE_DONATION,
  idResource,
  data,
  updateChallenge,
  updateUserPoints,
}: {
  dataPath?: string;
  idResource: number;
  data: any;
  updateUserPoints: (userPoints: number) => void;
  updateChallenge: (challengeData: IChallenge) => void;
}) => {
  try {
    const response = await api.putDataCall({
      dataPath,
      data,
      callConfig: {},
    });

    const { score, challenge } = response.data;

    if (typeof score === 'number') updateUserPoints(score);

    updateChallenge(challenge);
  } catch (err) {
    const { code, message } = err?.response?.data || {};

    if (code === '9999' && typeof message === 'number') {
      updateUserPoints(message);
    }
    return err;
  }
};

export const completeExperienceCheckout = async ({
  dataPath = apiPaths.CHALLENGES.CHECKOUT,
  data,
  updateChallenge,
  updateUserPoints,
}: {
  dataPath?: string;
  data: any;
  updateUserPoints: (userPoints: number) => void;
  updateChallenge: (challengeData: IChallenge) => void;
}) => {
  try {
    const response = await api.putDataCall({
      dataPath,
      data,
      callConfig: {},
    });

    const { score, challenge } = response.data;

    if ((score || score === 0) && typeof score === 'number')
      updateUserPoints(score);

    updateChallenge(challenge);
  } catch (err) {
    return err;
  }
};

export const checkCampaignActive = (
  resources: IChallengeResource[],
): boolean => {
  const triggerResources = resources
    .map(
      ({
        config: { isCampaignActivator, isCompleted } = {
          isCampaignActivator: false,
          isCompleted: false,
        },
      }) => {
        return { isCampaignActivator, isCompleted };
      },
    )
    .filter(({ isCampaignActivator }) => isCampaignActivator);

  // TODO preguntas sobre la activación de la campaña
  return (
    triggerResources.length >= 1 &&
    triggerResources.some(({ isCompleted }) => isCompleted)
  );
};

export const isDependentConditionSatisfied = (
  dependenciesList: IResourceDependency[],
  challengeResources: IChallengeResource[],
): boolean => {
  return dependenciesList?.every(({ idDependency, trigger }) => {
    const mainResource = challengeResources.find(
      ({ resource: { idResource } }) => idResource === idDependency,
    );

    switch (mainResource?.type) {
      case QUIZ:
      case QUIZ_DERMO:
        const { isQuizDone } = mainResource.config as IChallengeQuiz;

        switch (trigger) {
          case CampaignModuleStatusEnum.QUIZ_DONE_DEPRECATED:
          case CampaignModuleStatusEnum.QUIZ_DONE:
            return isQuizDone;
          case CampaignModuleStatusEnum.QUIZ_NOT_DONE:
            return !isQuizDone;
        }
        break;
      case ACTIVATION_BUTTON:
      case EXPERIENCE_ACTIVATION:
      case CHECKOUT:
      case CHECKOUT_LOTTERY:
      case CHECKOUT_DONATION:
      case QUIZ_TYPEFORM:
      case REDEEM_PRODUCT:
      case SHOPPING_CART:
      case SURVEY:
      case VIDEO:
        const { isCompleted } = mainResource.config as IChallengeResourceConfig;

        switch (trigger) {
          case CampaignModuleStatusEnum.GENERIC_DONE:
            return isCompleted;
          case CampaignModuleStatusEnum.GENERIC_NOT_DONE:
            return !isCompleted;
        }
        break;
      case EVENT:
        const { inscription, attend }: IChallengeEvent =
          mainResource.config as IChallengeEvent;

        switch (trigger) {
          case CampaignModuleStatusEnum.EVENT_NOT_ATTENDED:
            return inscription.isPerformed && !attend.isPerformed;
          case CampaignModuleStatusEnum.EVENT_ATTENDED:
            return inscription.isPerformed && attend.isPerformed;
          case CampaignModuleStatusEnum.EVENT_JOINED:
            return inscription.isPerformed || inscription.inWaitingList;
        }
        break;
      case CHALLENGE_MATERIALS:
      case LINKED_CHALLENGES:
        const { isCompleted: isPartialCompleted, isPartialDone } =
          mainResource.config as IChallengeResourceConfig;

        switch (trigger) {
          case CampaignModuleStatusEnum.PARTIAL_DONE:
            return isPartialCompleted;
          case CampaignModuleStatusEnum.PARTIAL_NOT_DONE:
            return !isPartialDone;
          case CampaignModuleStatusEnum.PARTIAL_PARTIALLY_DONE:
            return isPartialDone;
        }
        break;
    }

    return false;
  });
};

export const getResourceDetail = (
  resource: IResourceList,
  type: ResourceTypeDetailEnum,
): null | IResourceDetailList => {
  if (!resource?.resourceDetailList?.length) return null;

  return resource.resourceDetailList?.find(
    ({ status, idResourceTypeD: { idResourceTypeD } }: IResourceDetailList) =>
      status && idResourceTypeD === type,
  );
};

export const renderChallengeCard = ({
  challenge,
  isLinkedChallenge = false,
  isCarousel = false,
}: {
  challenge: ChallengeDetail;
  isLinkedChallenge?: boolean;
  isCarousel?: boolean;
}) => {
  const { challengeType, challengeId } = challenge;
  let cardComponent: JSX.Element;
  const isExperienceCarousel =
    isCarousel &&
    (challengeType === ChallengeTypeEnum.EXPERIENCE ||
      challengeType === ChallengeTypeEnum.FORMATION_CON ||
      challengeType === ChallengeTypeEnum.DONATION);

  const getSizeConfig = () => {
    if (isCarousel) return { span: 24 };
    switch (challengeType) {
      case ChallengeTypeEnum.CAMPAIGN:
      case ChallengeTypeEnum.EXPERIENCE:
      case ChallengeTypeEnum.FORMATION_CON:
      case ChallengeTypeEnum.CONTEST:
      case ChallengeTypeEnum.DONATION:
        return {
          xs: { span: 24 },
          sm: { span: 12 },
          md: { span: 12 },
          lg: { span: 12 },
          xl: { span: 8 },
        };
      case ChallengeTypeEnum.FORMATION:
      case ChallengeTypeEnum.TALK:
        return {
          xs: { span: 24 },
          sm: { span: 12 },
          md: { span: 12 },
          lg: { span: 8 },
          xl: { span: isLinkedChallenge ? 8 : 6 },
        };
    }
  };

  switch (challengeType) {
    case ChallengeTypeEnum.FORMATION:
      cardComponent = renderChallengeTraining(challenge);
      break;
    case ChallengeTypeEnum.TALK:
      cardComponent = renderChallengeTalk(challenge);
      break;
    case ChallengeTypeEnum.CAMPAIGN:
      cardComponent = renderChallengeCampaign(challenge);
      break;
    case ChallengeTypeEnum.EXPERIENCE:
    case ChallengeTypeEnum.DONATION:
      cardComponent = renderChallengeExperience(challenge);
      break;
    case ChallengeTypeEnum.CONTEST:
      cardComponent = renderChallengeContest(challenge);
      break;
    case ChallengeTypeEnum.FORMATION_CON:
      cardComponent = renderChallengeFormation(challenge);
      break;
  }

  return (
    cardComponent && (
      <Col
        id={`challenge-card--${challengeId}`}
        key={challengeId}
        className={`${isExperienceCarousel && 'experience-carousel-card'}`}
        {...getSizeConfig()}>
        {cardComponent}
      </Col>
    )
  );
};

export const setResourceGuidePointTitle = (resourceType) => {
  switch (resourceType) {
    case ResourceTypeEnum.SURVEY:
      return 'campaign.resource-guide.survey';
    case ResourceTypeEnum.EVENT:
      return 'campaign.resource-guide.event';
    case ResourceTypeEnum.LINKED_CHALLENGES:
      return 'campaign.resource-guide.linked-challenges';
    case ResourceTypeEnum.VIDEO:
      return 'campaign.resource-guide.video';
    case ResourceTypeEnum.SHOPPING_CART:
      return 'campaign.resource-guide.shopping-cart';
    case ResourceTypeEnum.CHALLENGE_MATERIALS:
      return 'campaign.resource-guide.challenge-materials';
  }
};

export const isCorrectCheckoutAddress = (
  address: IAddressData & IAddressDataChile,
) => {
  const { PLATFORM } = config.APP;
  if (address === null) return false;
  const {
    name,
    surname,
    address: addrName,
    zipCode,
    regionName,
    city,
  } = address;

  if (PLATFORM === PlatformEnum.CON_CHILE)
    return (
      name &&
      surname &&
      addrName &&
      zipCode &&
      regionName &&
      city &&
      address.district
    );

  return name && surname && addrName && zipCode && regionName && city;
};

export const getInitialCheckoutAddress = (values: object): IAddressData => {
  return {
    address: '',
    city: '',
    name: '',
    regionName: '',
    surname: '',
    zipCode: '',
    phone: '',
    deliveryInstructions: '',
    defaultAddress: false,
    ...values,
  };
};

export const checkUserResourcePoints = (
  userPoints: number,
  componentPoints: number,
  challengeType: ChallengeTypeEnum,
) => {
  if (
    challengeType === ChallengeTypeEnum.EXPERIENCE ||
    challengeType === ChallengeTypeEnum.DONATION
  ) {
    return Number(userPoints) >= Math.abs(componentPoints);
  }

  return Number(userPoints) >= Number(componentPoints);
};

//We check if the user has the right roles to make this challenge available.
export const isChallengeAvailable = (roles: IChallengeRole[], user: IUser) => {
  return roles.find((e) => {
    return user.roles.roles.find((role) => {
      if (role.idRoleType.idRoleType === RoleEnum.LOVER) return true;
      else if (e.roleType.idRoleType === role.idRoleType.idRoleType)
        return true;
    });
  });
};
